import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormulaireMouvementService } from '@services/formulaire-mouvement/formulaire-mouvement.service';
import { RequeteCoordonneesMouvementService } from '@services/requete-coordonnees-mouvement/requete-coordonnees-mouvement.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { GabaritService } from '@services/gabarit/gabarit.service';
import { GlobalStoreService } from '@services/global-store/global-store.service';
import { FormulaireCoordonneesMouvementAdresseModel } from '../../models/formulaire/formulaire-coordonnees-mouvement-adresse.model';
import { AdresseMouvement } from '../../api/model/adresse-mouvement.model';
import { AccessibiliteService } from '@services/accessibilite.service';
import { GestionTitreService } from '@services/navigation/gestion-titre.service';
import { ActionDGAGEnum } from 'src/app/constantes/action-dgag.enum';
import { MouvementUtil } from 'src/app/util/mouvement-util';
import { CybermetrieCoordonneesService } from '@services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../constantes/cybermetrie.enum';

@Component({
  selector: 'app-page-validation',
  templateUrl: './page-validation-adresse.component.html',
  standalone: false,
})
export class PageValidationAdresseComponent implements OnInit, AfterViewInit {
  isAppMobile?: boolean;
  form?: FormGroup<FormulaireCoordonneesMouvementAdresseModel> | null;
  dateDebutUtilisation: string = '';

  private readonly formulaireMouvementService = inject(FormulaireMouvementService);
  private readonly requeteCoordonneesMouvementService = inject(RequeteCoordonneesMouvementService);
  private readonly navigationService = inject(NavigationService);
  private readonly accessibiliteService = inject(AccessibiliteService);
  private readonly gabaritService = inject(GabaritService);
  private readonly gestionTitreService = inject(GestionTitreService);
  private readonly globalStoreService = inject(GlobalStoreService);
  private readonly cybermetrieCoordonneesService = inject(CybermetrieCoordonneesService);

  constructor() {}

  ngOnInit(): void {
    this.isAppMobile = this.gabaritService.isAppMobile();
    this.form = this.formulaireMouvementService.getFormAdresse();
    this.dateDebutUtilisation = this.form?.value.dateDebutUtilisation ?? '';
    this.gestionTitreService.addTitreOnglet('titreOnglet.pageValidation');

    this.cybermetrieCoordonneesService.transactionEtape(
      CybermetrieEnum.LABEL_MODIFIER_ADRESSE,
      this.cybermetrieAttributAppariementDGAG(),
    );
  }

  ngAfterViewInit(): void {
    this.accessibiliteService.setFocus('#titreValidation');
  }

  get adresseSaisie(): AdresseMouvement {
    return MouvementUtil.toAdresseMouvement(this.form);
  }

  get preferenceAppel(): string {
    return this.form?.value.preferenceAppelAssurances === 'true'
      ? 'modifierAdresse.questionnaireDGAG.preferenceAppel.oui'
      : 'modifierAdresse.questionnaireDGAG.preferenceAppel.non';
  }

  get preferenceMoment(): string {
    return (
      'modifierAdresse.questionnaireDGAG.preferenceMoment.' +
      (this.form?.value.preferenceMomentAssurances ?? 'aucun')
    );
  }

  get afficherDetention(): boolean {
    return this.form?.value.detientAssurances === 'false';
  }

  get afficherChangementFait(): boolean {
    return this.form?.value.changementAssurancesFait === 'true';
  }

  get afficherAppel(): boolean {
    return this.form?.value.preferenceAppelAssurances != null;
  }

  get afficherMoment(): boolean {
    return this.form?.value.preferenceMomentAssurances != null;
  }

  naviguerVersModifierAdresse(): void {
    this.navigationService.naviguerVersModifierAdresse();
    this.cybermetrieCoordonneesService.actionClick(
      CybermetrieEnum.LABEL_MODIFIER_ADRESSE_VALIDATION,
      this.cybermetrieAttributAppariementDGAG(),
      CybermetrieEnum.EXTRA_ATTRIBUT_ADRESSE,
    );
  }

  naviguerVersModifierAssurances(): void {
    this.navigationService.naviguerVersModifierAdresseAssurances();
    this.cybermetrieCoordonneesService.actionClick(
      CybermetrieEnum.LABEL_MODIFIER_ADRESSE_VALIDATION,
      this.cybermetrieAttributAppariementDGAG(),
      CybermetrieEnum.EXTRA_ATTRIBUT_ASSURANCE,
    );
  }

  naviguerVersConfirmation(): void {
    const requete = this.requeteCoordonneesMouvementService.construireRequeteModifierAdresse();
    this.globalStoreService.modifierCoordonneMouvement$(requete).subscribe({
      next: () => {
        this.navigationService.naviguerVersConfirmationAdresse(
          this.contexteDGAG(),
          this.form?.value.detentionDGAGApparie ?? false,
        );
      },
    });
  }

  annuler(): void {
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }

  cybermetrieAttributAppariementDGAG(): string {
    return this.form?.value.detentionDGAGApparie === true
      ? CybermetrieEnum.EXTRA_ATTRIBUT_DGAG_APPARIE
      : CybermetrieEnum.EXTRA_ATTRIBUT_DGAG_NON_APPARIE;
  }

  contexteDGAG(): ActionDGAGEnum {
    if (this.form?.value.changementAssurancesFait === 'false') {
      if (this.afficherMoment) {
        return ActionDGAGEnum.RECEVOIR_APPEL;
      }

      if (this.form?.value.preferenceAppelAssurances === 'false') {
        return ActionDGAGEnum.APPELER;
      }
    }

    return ActionDGAGEnum.AUCUNE_ACTION;
  }
}
